/**
 * Add a cashier by name — and show their PIN, once.
 *
 * A cashier is not a login. They are identified at the register by their name
 * and a PIN, never see a sign-in screen, and mostly have no work email. The
 * invite flow insists on an address, a role picker and an app scope for every
 * account, so a shop's four cashiers were being set up as one shared account,
 * which made every shift and every void read as the same person.
 *
 * This asks for a name. The server mints the account, the PIN and the Cashier
 * scope, and the PIN is shown ONCE, to be written on the slip that goes to the
 * person. It is hashed on the row; nobody can read it back.
 *
 * Moved here from Cashier's settings page so the Team panel offers the same
 * flow; the behaviour and test ids are unchanged.
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Check, Copy, Loader2 } from 'lucide-react'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Checkbox } from '../ui/checkbox'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { nameError, emailError } from '../../lib/validation'
import { cashierApi, type CashierCreated } from '../../pos/api'
import type { TeamBranch } from './teamModel'

export const CASHIER_ROLES = [
  { value: 'branch_manager', label: 'Cashier', hint: 'Runs the till: sells, voids, opens and closes shifts' },
  { value: 'manager', label: 'Manager', hint: 'Cashier, plus reports and the back office' },
]

const EMPTY = { name: '', role: 'branch_manager', email: '', branch_ids: [] as number[] }

export default function AddCashierDialog({
  open, onOpenChange, branches, onCreated,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  branches: TeamBranch[]
  /** Called after the account exists; the PIN reveal is handled here. */
  onCreated: () => void
}) {
  const { t } = useTranslation()
  const [form, setForm] = useState({ ...EMPTY })
  const [saving, setSaving] = useState(false)
  const [minted, setMinted] = useState<CashierCreated | null>(null)

  const nameProblem = nameError(form.name, 'any', { required: true, max: 150, label: 'Name' })
  const emailProblem = emailError(form.email)

  const create = async () => {
    const problem = nameProblem ?? emailProblem
    if (problem) { toast.error(problem); return }
    setSaving(true)
    try {
      const made = await cashierApi.quickCreate({
        name: form.name.trim(),
        role: form.role,
        email: form.email.trim() || undefined,
        branch_ids: form.branch_ids,
      })
      // The form's job is done; the PIN takes the whole screen on its own.
      onOpenChange(false)
      setMinted(made)
      setForm({ ...EMPTY })
      onCreated()
    } catch (err: any) {
      toast.error(err?.response?.data?.detail ?? t('common.saveFailed', 'Could not add that cashier.'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={o => { if (!saving) onOpenChange(o) }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('pos.settings.addCashier', 'Add cashier')}</DialogTitle>
            <DialogDescription>
              {t('pos.settings.addCashierHint', 'A name is enough. They get a PIN for the till and nothing else to remember.')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-1.5">
              <Label>{t('common.name', 'Name')} <span className="text-destructive">*</span></Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                     placeholder="e.g. Sara Al-Amri" autoFocus data-testid="cashier-name" />
            </div>
            <div className="grid gap-1.5">
              <Label>{t('users.role', 'Role')}</Label>
              <Select value={form.role} onValueChange={v => setForm(f => ({ ...f, role: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CASHIER_ROLES.map(r => (
                    <SelectItem key={r.value} value={r.value}>
                      <span className="font-medium">{r.label}</span>
                      <span className="text-muted-foreground"> — {r.hint}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {branches.length > 1 && (
              <div className="grid gap-1.5">
                <Label>{t('branches.title', 'Branches')}</Label>
                <div className="flex flex-wrap gap-x-4 gap-y-2">
                  {branches.map(b => (
                    <label key={b.id} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={form.branch_ids.includes(b.id)}
                        onCheckedChange={v => setForm(f => ({
                          ...f,
                          branch_ids: v === true ? [...f.branch_ids, b.id] : f.branch_ids.filter(x => x !== b.id),
                        }))}
                      />
                      {b.name}
                    </label>
                  ))}
                </div>
                <p className="text-[11px] text-muted-foreground">
                  {t('pos.settings.branchesHint', 'None ticked = every branch.')}
                </p>
              </div>
            )}
            <div className="grid gap-1.5">
              <Label>{t('users.email', 'Email')} <span className="text-muted-foreground">({t('common.optional', 'optional')})</span></Label>
              <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                     placeholder={t('pos.settings.emailHint', 'Only if they will also use the back office')} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>{t('common.cancel', 'Cancel')}</Button>
            <Button onClick={() => void create()} disabled={saving} className="gap-2" data-testid="cashier-create">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('pos.settings.addCashier', 'Add cashier')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* The PIN, once. Closing it is the only way on, so nobody dismisses it
          by accident and loses the digits. */}
      <PinReveal minted={minted} onDone={() => setMinted(null)} />
    </>
  )
}

function PinReveal({ minted, onDone }: { minted: CashierCreated | null; onDone: () => void }) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  useEffect(() => { setCopied(false) }, [minted?.pos_pin])

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(minted?.pos_pin ?? '')
      setCopied(true)
    } catch {
      toast.error(t('common.copyFailed', 'Could not copy — write it down.'))
    }
  }

  return (
    <Dialog open={!!minted} onOpenChange={o => { if (!o) onDone() }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{minted?.name}</DialogTitle>
          <DialogDescription>
            {t('pos.settings.pinOnce', 'This PIN is shown once. Give it to them now — it cannot be looked up later, only replaced.')}
          </DialogDescription>
        </DialogHeader>
        <div className="my-2 flex items-center justify-center gap-3">
          <span className="rounded-xl bg-muted px-6 py-4 font-mono text-4xl font-bold tracking-[0.3em]">
            <span data-testid="cashier-pin">{minted?.pos_pin}</span>
          </span>
          <Button variant="outline" size="icon" onClick={() => void copy()} aria-label={t('common.copy', 'Copy')}>
            {copied ? <Check className="h-4 w-4 text-success-fg" /> : <Copy className="h-4 w-4" />}
          </Button>
        </div>
        <DialogFooter>
          <Button onClick={onDone} className="w-full">{t('pos.settings.pinNoted', "I've given them the PIN")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
